import React, { useEffect, useState } from "react";
import apiService from "../../ApiService";
import { toast } from "react-toastify";
import Select from "react-select";

const UpdateTransactionCategory = () => {
  // Get current month's first and last day
  const getCurrentMonthDates = () => {
    const now = new Date();
    const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
    const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0);

    const formatDate = (date) => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");
      return `${year}-${month}-${day}`;
    };

    return {
      from: formatDate(firstDay),
      to: formatDate(lastDay),
    };
  };

  const currentMonthDates = getCurrentMonthDates();

  const [fromDate, setFromDate] = useState(currentMonthDates.from);
  const [toDate, setToDate] = useState(currentMonthDates.to);
  const [transactions, setTransactions] = useState([]);
  const [bankFilter, setBankFilter] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [newCategory, setNewCategory] = useState("");
  const [loading, setLoading] = useState(false);
  const [pageIndex, setPageIndex] = useState(1);
  const [pageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(1);

  // Filter options state
  const [bankOptions, setBankOptions] = useState([]);
  const [typeOptions, setTypeOptions] = useState([]);
  const [categoryOptions, setCategoryOptions] = useState([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [newCountry, setNewCountry] = useState("");
  const [newTxDate, setNewTxDate] = useState("");
  const [newTxBank, setNewTxBank] = useState("");
  const [newTxType, setNewTxType] = useState("");
  const [newTxAmount, setNewTxAmount] = useState("");
  const [newTxCategory, setNewTxCategory] = useState("");
  const [newTxDescription, setNewTxDescription] = useState("");
  const handleAddTransaction = async () => {
    if (!newTxDate || !newTxBank || !newTxType || !newTxAmount) {
      toast.error("Please fill in required fields");
      return;
    }
    try {
      const payload = {
        country: newCountry,
        transaction_date: newTxDate,
        bank_name: newTxBank,
        transaction_type: newTxType,
        amount: parseFloat(newTxAmount),
        category: newTxCategory,
        description: newTxDescription,
      };
      const res = await apiService.createTransaction(payload);

      if (
        res &&
        (res.success === true ||
          (typeof res.message === "string" &&
            res.message.toLowerCase().includes("transaction added")) ||
          (res.id !== undefined && res.id !== null))
      ) {
        toast.success("Transaction added successfully!");
        setShowAddModal(false);
        fetchTransactions();
        // Reset form
        setNewCountry("");
        setNewTxDate("");
        setNewTxBank("");
        setNewTxType("");
        setNewTxAmount("");
        setNewTxCategory("");
        setNewTxDescription("");
      } else {
        toast.error("Failed to add transaction");
      }
    } catch (error) {
      toast.error("Error adding transaction: " + error.message || error);
    }
  };

  const fetchTransactions = async () => {
    setLoading(true);
    try {
      const data = await apiService.fetchTransactions({
        fromDate: fromDate || null,
        toDate: toDate || null,
        pageIndex,
        pageSize,
        bankName: bankFilter || null,
        type: typeFilter || null,
        category: categoryFilter || null,
      });

      let txArr = [];
      if (Array.isArray(data)) {
        txArr = data;
      } else if (Array.isArray(data.records)) {
        txArr = data.records;
      } else if (Array.isArray(data.data)) {
        txArr = data.data;
      }

      setTransactions(txArr);
      setTotalPages(Number(data.totalPages || data.total_pages || 1));
      if (typeof data.page === "number") {
        setPageIndex(data.page);
      }
    } catch (error) {
      toast.error("Failed to fetch transactions");
    }
    setLoading(false);
  };

  const loadFilterOptions = async () => {
    try {
      const banks = await apiService.getDistinctValues("bank");
      const types = await apiService.getDistinctValues("type");
      const categories = await apiService.getDistinctValues("category");
      setBankOptions(Array.isArray(banks) ? banks : []);
      setTypeOptions(Array.isArray(types) ? types : []);
      setCategoryOptions(Array.isArray(categories) ? categories : []);
    } catch (error) {
      toast.error("Failed to load filter options");
    }
  };

  useEffect(() => {
    loadFilterOptions();
  }, []);

  useEffect(() => {
    if (fromDate && toDate) {
      fetchTransactions();
    }
    // eslint-disable-next-line
  }, [fromDate, toDate, pageIndex, bankFilter, typeFilter, categoryFilter]);

  const handleUpdateCategory = async () => {
    if (!selectedTransaction || !newCategory) return;
    try {
      const id = Number(selectedTransaction.id || selectedTransaction.ID);
      const category = String(newCategory).trim();
      if (!id || !category) {
        toast.error("Missing id or category");
        return;
      }
      const updatePayload = { id, category };
      const res = await apiService.updateTransaction(id, updatePayload);
      let success = false;
      if (res && typeof res === "object" && (res.message || res.success)) {
        if (
          (typeof res.message === "string" &&
            res.message.toLowerCase().includes("success")) ||
          res.success === true
        ) {
          success = true;
        }
      }
      if (success) {
        toast.success("Transaction category updated!");
        fetchTransactions();
        setSelectedTransaction(null);
        setNewCategory("");
      } else {
        toast.error("Failed to update transaction category");
      }
    } catch (error) {
      toast.error("Failed to update transaction category");
    }
  };

  return (
    <div className="max-w-7xl mx-auto bg-white">
      {/* Enhanced Filter Section */}
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-6 mb-6 border border-blue-100">
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
          <span className="text-xl mr-2">🔍</span>
          Filter Transactions
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              From Date
            </label>
            <input
              type="date"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              className="w-full border border-gray-300 px-3 py-2 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              To Date
            </label>
            <input
              type="date"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
              className="w-full border border-gray-300 px-3 py-2 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Bank
            </label>
            <select
              className="w-full border border-gray-300 px-3 py-2 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all bg-white"
              value={bankFilter}
              onChange={(e) => {
                setBankFilter(e.target.value);
                setPageIndex(1);
              }}
            >
              <option value="">All Banks</option>
              {bankOptions.map((opt) => (
                <option key={opt} value={opt}>
                  {opt}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Type
            </label>
            <select
              className="w-full border border-gray-300 px-3 py-2 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all bg-white"
              value={typeFilter}
              onChange={(e) => {
                setTypeFilter(e.target.value);
                setPageIndex(1);
              }}
            >
              <option value="">All Types</option>
              {typeOptions.map((opt) => (
                <option key={opt} value={opt}>
                  {opt}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Category
            </label>
            <Select
              options={[
                { value: "", label: "All Categories" },
                ...categoryOptions.map((opt) => ({ value: opt, label: opt })),
              ]}
              value={
                categoryFilter
                  ? { value: categoryFilter, label: categoryFilter }
                  : { value: "", label: "All Categories" }
              }
              onChange={(selected) => {
                setCategoryFilter(selected?.value || "");
                setPageIndex(1);
              }}
              isClearable
              placeholder="Search or select category..."
              className="react-select-container"
              classNamePrefix="react-select"
              styles={{
                control: (base) => ({
                  ...base,
                  borderColor: "#d1d5db",
                  borderRadius: "0.5rem",
                  padding: "0.125rem",
                  "&:hover": {
                    borderColor: "#3b82f6",
                  },
                }),
              }}
            />
          </div>
        </div>

        <div className="mt-4 flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <div className="text-sm text-gray-600">
              <span className="font-semibold">Date Range:</span> {fromDate} to{" "}
              {toDate}
            </div>
            <button
              className="text-sm bg-blue-100 hover:bg-blue-200 text-blue-700 px-4 py-1.5 rounded-lg transition-all"
              onClick={() => {
                const dates = getCurrentMonthDates();
                setFromDate(dates.from);
                setToDate(dates.to);
              }}
              type="button"
            >
              📅 Reset to Current Month
            </button>
          </div>
          <button
            className="bg-green-600 hover:bg-green-700 text-white px-6 py-2.5 rounded-lg transition-all font-medium shadow-sm hover:shadow-md flex items-center space-x-2"
            onClick={() => setShowAddModal(true)}
            type="button"
          >
            <span className="text-lg">+</span>
            <span>Add Transaction</span>
          </button>
        </div>
      </div>

      {/* Table */}
      {loading ? (
        <div>Loading...</div>
      ) : (
        <table className="w-full border mb-4">
          <thead>
            <tr className="bg-gray-100">
              <th className="border px-2 py-1">Date</th>
              <th className="border px-2 py-1">Bank</th>
              <th className="border px-2 py-1">Type</th>
              <th className="border px-2 py-1">Amount</th>
              <th className="border px-2 py-1">Category</th>
              <th className="border px-2 py-1">Description</th>
              <th className="border px-2 py-1">Action</th>
            </tr>
          </thead>
          <tbody>
            {transactions.length === 0 && (
              <tr>
                <td colSpan={7} className="text-center">
                  No transactions found
                </td>
              </tr>
            )}
            {transactions.map((tx) => (
              <tr
                key={tx.id}
                className={
                  selectedTransaction && selectedTransaction.id === tx.id
                    ? "bg-blue-50"
                    : ""
                }
              >
                <td className="border px-2 py-1">
                  {tx.transaction_date || tx.date || tx.Date || ""}
                </td>
                <td className="border px-2 py-1">{tx.bank_name || ""}</td>
                <td className="border px-2 py-1">
                  {tx.transaction_type || ""}
                </td>
                <td className="border px-2 py-1">
                  {tx.amount || tx.Amount || ""}
                </td>
                <td className="border px-2 py-1">
                  {tx.category || tx.Category || ""}
                </td>
                <td className="border px-2 py-1">
                  {tx.description || tx.Description || ""}
                </td>
                <td className="border px-2 py-1">
                  <button
                    className="text-blue-600 underline"
                    onClick={() => {
                      setSelectedTransaction(tx);
                      setNewCategory(tx.category || tx.Category || "");
                    }}
                  >
                    Update
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {/* Pagination */}
      <div className="flex gap-2 mb-4">
        <button
          className="px-2 py-1 border rounded"
          disabled={pageIndex <= 1}
          onClick={() => setPageIndex(pageIndex - 1)}
        >
          Prev
        </button>
        <span>
          Page {pageIndex} of {totalPages}
        </span>
        <button
          className="px-2 py-1 border rounded"
          disabled={pageIndex >= totalPages}
          onClick={() => setPageIndex(pageIndex + 1)}
        >
          Next
        </button>
      </div>

      {/* Update form */}
      {selectedTransaction && (
        <div className="border p-4 rounded bg-gray-50 mb-4">
          <h3 className="font-semibold mb-2">
            Update Category for Transaction #{selectedTransaction.id}
          </h3>
          <div className="flex gap-2 items-center mb-4">
            <span>Current Category:</span>
            <span className="font-bold">
              {selectedTransaction.category || selectedTransaction.Category}
            </span>
          </div>
          <div className="flex gap-2 items-center">
            <div className="min-w-[200px]">
              <Select
                options={categoryOptions.map((cat) => ({
                  label: cat,
                  value: cat,
                }))}
                value={
                  newCategory
                    ? { label: newCategory, value: newCategory }
                    : null
                }
                onChange={(option) =>
                  setNewCategory(option ? option.value : "")
                }
                placeholder="Select Category"
                isClearable
                isSearchable
              />
            </div>

            <button
              className="bg-blue-600 text-white px-4 py-1 rounded"
              onClick={handleUpdateCategory}
            >
              Update
            </button>
            <button
              className="px-4 py-1 border rounded"
              onClick={() => setSelectedTransaction(null)}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {showAddModal && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-[9999]"
          onClick={() => setShowAddModal(false)}
        >
          <div
            className="bg-white rounded-lg shadow-lg max-w-md w-full p-6 mx-4"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 className="text-xl font-semibold mb-4">Add New Transaction</h2>

            {/* inside modal form div */}
            <div className="flex flex-col gap-4">
              <label className="flex items-center gap-4">
                <span className="w-28 font-semibold">
                  Date<span className="text-red-600">*</span>
                </span>
                <input
                  type="date"
                  value={newTxDate}
                  onChange={(e) => setNewTxDate(e.target.value)}
                  className="border px-2 py-1 rounded flex-grow"
                />
              </label>

              <label className="flex items-center gap-4">
                <span className="w-28 font-semibold">
                  Bank<span className="text-red-600">*</span>
                </span>
                <select
                  value={newTxBank}
                  onChange={(e) => setNewTxBank(e.target.value)}
                  className="border px-2 py-1 rounded flex-grow"
                >
                  <option value="">Select Bank</option>
                  {bankOptions.map((bank) => (
                    <option key={bank} value={bank}>
                      {bank}
                    </option>
                  ))}
                </select>
              </label>

              <label className="flex items-center gap-4">
                <span className="w-28 font-semibold">
                  Type<span className="text-red-600">*</span>
                </span>
                <select
                  value={newTxType}
                  onChange={(e) => setNewTxType(e.target.value)}
                  className="border px-2 py-1 rounded flex-grow"
                >
                  <option value="">Select Type</option>
                  {typeOptions.map((type) => (
                    <option key={type} value={type}>
                      {type}
                    </option>
                  ))}
                </select>
              </label>

              <label className="flex items-center gap-4 mt-4">
                <span className="w-28 font-semibold">
                  Country<span className="text-red-600">*</span>
                </span>
                <select
                  value={newCountry}
                  onChange={(e) => setNewCountry(e.target.value)}
                  className="border px-2 py-1 rounded flex-grow"
                >
                  <option value="">Select Country</option>
                  <option value="IND">IND</option>
                  <option value="USA">USA</option>
                </select>
              </label>

              <label className="flex items-center gap-4">
                <span className="w-28 font-semibold">
                  Amount<span className="text-red-600">*</span>
                </span>
                <input
                  type="number"
                  value={newTxAmount}
                  onChange={(e) => setNewTxAmount(e.target.value)}
                  className="border px-2 py-1 rounded flex-grow"
                  step="0.01"
                />
              </label>

              <label className="flex items-center gap-4">
                <span className="w-28 font-semibold">Category</span>
                <div className="flex-grow">
                  <Select
                    options={categoryOptions.map((cat) => ({
                      label: cat,
                      value: cat,
                    }))}
                    value={
                      newTxCategory
                        ? { label: newTxCategory, value: newTxCategory }
                        : null
                    }
                    onChange={(option) =>
                      setNewTxCategory(option ? option.value : "")
                    }
                    isClearable
                    isSearchable
                    placeholder="Select Category"
                  />
                </div>
              </label>

              <label className="flex items-start gap-4">
                <span className="w-28 font-semibold pt-1">Description</span>
                <textarea
                  value={newTxDescription}
                  onChange={(e) => setNewTxDescription(e.target.value)}
                  className="border px-2 py-1 rounded flex-grow"
                  rows={3}
                  placeholder="Optional description"
                />
              </label>
            </div>

            <div className="mt-4 flex justify-end gap-2">
              <button
                className="px-4 py-2 border rounded"
                onClick={() => setShowAddModal(false)}
                type="button"
              >
                Cancel
              </button>
              <button
                className="bg-blue-600 text-white px-4 py-2 rounded"
                onClick={handleAddTransaction}
                type="button"
              >
                Add
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UpdateTransactionCategory;
